import { useCallback, useMemo } from 'react';
import type {
  AccontTypeData,
  AccountType,
  MarginAccount,
  RegularAccount,
} from '@/entities/account';
import type {
  InternalAccountSectionCardType,
  SectionType,
} from '@/features/internal-transfer';

import WalletIcon from '@/shared/assets/wallet-icon.svg?react';
import InvestAccountIcon from '@/shared/assets/invest-account-icon.svg?react';
import SpotAccountIcon from '@/shared/assets/spot-account-icon.svg?react';
import PercentIcon from '@/shared/assets/percent-icon.svg?react';
import EarnAccountIcon from '@/shared/assets/earn-account-icon.svg?react';
import LogoSmallIcon from '@/shared/assets/f2ex-logo-small.svg?react';
import { useTranslation } from 'react-i18next';

export const baseOrders = {
  from: {
    ewallet: 0,
    invest: 1,
    spot: 2,
    leverage: 3,
    earn: 4,
  },
  to: {
    invest: 0,
    spot: 1,
    leverage: 2,
    ewallet: 3,
    earn: 4,
  },
} as const;

interface Args {
  initialFromAccountType?: AccountType;
  initialToAccountType?: AccountType;
  ewallet: AccontTypeData<RegularAccount> | null;
  invest: AccontTypeData<MarginAccount> | null;
  spot: AccontTypeData<RegularAccount> | null;
  leverage: AccontTypeData<MarginAccount> | null;
  earn: AccontTypeData<MarginAccount> | null;
}

export const useInternalTransferCardOrders = ({
  initialFromAccountType,
  initialToAccountType,
  ewallet,
  leverage,
  spot,
  invest,
  earn,
}: Args) => {
  const { t } = useTranslation();

  const accountsByType: Array<InternalAccountSectionCardType> = useMemo(() => {
    return [
      {
        type: 'ewallet',
        title: t('internalTransferModal.ewallet'),
        icon: WalletIcon,
        label: LogoSmallIcon,
        data: ewallet,
        isPending: false,
        exists: !!ewallet && ewallet.accounts.length > 0,
      },
      {
        type: 'spot',
        title: t('internalTransferModal.spot'),
        icon: SpotAccountIcon,
        label: LogoSmallIcon,
        data: spot,
        isPending: false,
        exists: !!spot && spot.accounts.length > 0,
      },
      {
        type: 'invest',
        title: t('internalTransferModal.invest'),
        icon: InvestAccountIcon,
        label: LogoSmallIcon,
        data: invest,
        isPending: false,
        exists: !!invest && invest.accounts.length > 0,
      },
      {
        type: 'leverage',
        title: t('internalTransferModal.leverage'),
        icon: PercentIcon,
        label: LogoSmallIcon,
        data: leverage,
        isPending: false,
        exists: !!leverage && leverage.accounts.length > 0,
      },
      {
        type: 'earn',
        title: t('personalAccount.accountsSection.earnTitle'),
        icon: EarnAccountIcon,
        label: LogoSmallIcon,
        data: earn,
        isPending: false,
        exists: !!earn && earn.accounts.length > 0,
      },
    ];
  }, [t, ewallet, invest, spot, leverage, earn]);

  const getAccountsForSection = useCallback(
    (section: SectionType) => {
      const initialType =
        section === 'from' ? initialFromAccountType : initialToAccountType;

      const oppositeInitialType =
        section === 'from' ? initialToAccountType : initialFromAccountType;

      const order = baseOrders[section];

      return [...accountsByType].sort((a, b) => {
        if (initialType) {
          if (a.type === initialType && b.type !== initialType) return -1;
          if (b.type === initialType && a.type !== initialType) return 1;
        }

        if (
          oppositeInitialType &&
          a.type === oppositeInitialType &&
          b.type !== oppositeInitialType
        ) {
          return 1;
        }

        if (
          oppositeInitialType &&
          b.type === oppositeInitialType &&
          a.type !== oppositeInitialType
        ) {
          return -1;
        }

        return order[a.type] - order[b.type];
      });
    },
    [accountsByType, initialFromAccountType, initialToAccountType]
  );

  const {
    accountsFrom,
    accountsTo,
    availableAccountTypesFrom,
    availableAccountTypesTo,
  } = useMemo(() => {
    const from = getAccountsForSection('from');
    const to = getAccountsForSection('to');

    return {
      accountsFrom: from,
      accountsTo: to,
      availableAccountTypesFrom: from
        .filter(type => type.exists)
        .map(type => type.type),
      availableAccountTypesTo: to
        .filter(type => type.exists)
        .map(type => type.type),
    };
  }, [
    getAccountsForSection,
    accountsByType,
    initialFromAccountType,
    initialToAccountType,
  ]);

  return {
    accountsFrom,
    accountsTo,
    availableAccountTypesFrom,
    availableAccountTypesTo,
  };
};
